import { PageErrorBoundary } from "./components/PageErrorBoundary";
import { PageLoading } from "./components/ui/PageState";
import { recoverStaleAssets } from "./lib/deployRecovery";
import { Navigate, Route, Routes, useLocation, type Location } from "react-router-dom";
import { lazy, Suspense, useEffect, useState } from "react";
import { AppShell } from "./components/AppShell";
import { RequireAuth } from "./components/RequireAuth";
import { useAuth } from "./contexts/AuthContext";

/** 배포 후 이전 빌드 청크가 404일 때 한 번 새로고침해 최신 청크를 받도록 함 */
function lazyWithChunkReload<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await importFn();
    } catch (e) {
      const err = e as Error;
      const isChunkError =
        err?.message?.includes?.("Failed to fetch dynamically imported module") ||
        err?.message?.includes?.("Importing a module script failed") ||
        (err?.name === "TypeError" && err?.message?.includes?.("Failed to fetch"));
      if (isChunkError && typeof window !== "undefined" && recoverStaleAssets()) {
        return new Promise<{ default: T }>(() => { });
      }
      throw e;
    }
  });
}

// 페이지 전환 시 자동으로 상단으로 스크롤
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // 경로가 변경되면 즉시 상단으로 스크롤 (로딩 전에 실행)
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);

  return null;
}

const StudentActivityPortal = lazyWithChunkReload(() => import("./pages/StudentActivityPortal"));
const PublicActivityRanking = lazyWithChunkReload(() => import("./pages/PublicActivityRanking"));

// Critical pages (loaded immediately)
import { Dashboard } from "./pages/Dashboard";
import { Login } from "./pages/Login";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { SupabaseAuthCallback } from "./pages/SupabaseAuthCallback";

const UpdatePassword = lazyWithChunkReload(() => import("./pages/UpdatePassword").then(m => ({ default: m.UpdatePassword })));

// Lazy-loaded pages (code splitting) — 청크 404 시 자동 새로고침
const Notifications = lazyWithChunkReload(() => import("./pages/Notifications").then(m => ({ default: m.Notifications })));
const ProfileSettings = lazyWithChunkReload(() => import("./pages/ProfileSettings").then(m => ({ default: m.ProfileSettings })));
const Messages = lazyWithChunkReload(() => import("./pages/Messages").then(m => ({ default: m.Messages })));
const MessageDetail = lazyWithChunkReload(() => import("./pages/MessageDetail").then(m => ({ default: m.MessageDetail })));
const ComposeMessage = lazyWithChunkReload(() => import("./pages/ComposeMessage").then(m => ({ default: m.ComposeMessage })));
const Follows = lazyWithChunkReload(() => import("./pages/Follows").then(m => ({ default: m.Follows })));
const Points = lazyWithChunkReload(() => import("./pages/Points").then(m => ({ default: m.Points })));
const Guide = lazyWithChunkReload(() => import("./pages/Guide").then(m => ({ default: m.Guide })));

// Workmate pages
const WorkmateTeacherJobs = lazyWithChunkReload(() => import("./pages/WorkmateTeacherJobs"));
const WorkmateSchoolDocuments = lazyWithChunkReload(() => import("./pages/WorkmateSchoolDocuments"));
const WorkmateSchoolConversation = lazyWithChunkReload(() => import("./pages/WorkmateSchoolConversation"));
const PosterStudio = lazyWithChunkReload(() => import("./pages/workmate/poster-studio/PosterStudio").then(m => ({ default: m.PosterStudio })));
const WorkmateHub = lazyWithChunkReload(() => import("./pages/WorkmateHub").then(m => ({ default: m.WorkmateHub })));
const StudentRecord = lazyWithChunkReload(() => import("./pages/StudentRecord").then(m => ({ default: m.StudentRecord })));
const RecordStudio = lazyWithChunkReload(() => import("./pages/classmate/record-studio/RecordStudio").then(m => ({ default: m.RecordStudio })));
const StudentRecordHistory = lazyWithChunkReload(() => import("./pages/StudentRecordHistory").then(m => ({ default: m.StudentRecordHistory })));
const EvaluationPlan = lazyWithChunkReload(() => import("./pages/EvaluationPlan").then(m => ({ default: m.EvaluationPlan })));
const ExamScore = lazyWithChunkReload(() => import("./pages/ExamScore").then(m => ({ default: m.ExamScore })));
const CustomGptList = lazyWithChunkReload(() => import("./pages/CustomGptList").then(m => ({ default: m.CustomGptList })));
const CustomGptDetail = lazyWithChunkReload(() => import("./pages/CustomGptDetail").then(m => ({ default: m.CustomGptDetail })));
const CustomGptCreate = lazyWithChunkReload(() => import("./pages/CustomGptCreate").then(m => ({ default: m.CustomGptCreate })));
const WorkmateFileConverter = lazyWithChunkReload(() => import("./pages/WorkmateFileConverter").then(m => ({ default: m.WorkmateFileConverter })));
const WorkmateWorksheetRestore = lazyWithChunkReload(() => import("./pages/WorkmateWorksheetRestore").then(m => ({ default: m.WorkmateWorksheetRestore })));
const WorkmateQuestionBank = lazyWithChunkReload(() => import("./pages/WorkmateQuestionBank").then(m => ({ default: m.WorkmateQuestionBank })));
const WorkmateMathDiagram = lazyWithChunkReload(() => import("./pages/WorkmateMathDiagram").then(m => ({ default: m.WorkmateMathDiagram })));
const WorkmatePurchaseOrder = lazyWithChunkReload(() => import("./pages/WorkmatePurchaseOrder").then(m => ({ default: m.WorkmatePurchaseOrder })));
const ReceiptsTopics = lazyWithChunkReload(() => import("./pages/ReceiptsTopics").then(m => ({ default: m.ReceiptsTopics })));
const ReceiptsTopic = lazyWithChunkReload(() => import("./pages/ReceiptsTopic").then(m => ({ default: m.ReceiptsTopic })));
const ReceiptsUpload = lazyWithChunkReload(() => import("./pages/ReceiptsUpload").then(m => ({ default: m.ReceiptsUpload })));
const ReClass = lazyWithChunkReload(() => import("./pages/ReClass").then(m => ({ default: m.ReClass })));
const LatexToHwp = lazyWithChunkReload(() => import("./pages/LatexToHwp").then(m => ({ default: m.LatexToHwp })));
const WorkmateTimetable = lazyWithChunkReload(() => import("./pages/WorkmateTimetable").then(m => ({ default: m.WorkmateTimetable })));
const EssayGradingList = lazyWithChunkReload(() => import("./pages/EssayGradingList").then(m => ({ default: m.EssayGradingList })));
const EssayGradingDetail = lazyWithChunkReload(() => import("./pages/EssayGradingDetail").then(m => ({ default: m.EssayGradingDetail })));

// Registry pages
const WorkmateRegistryList = lazyWithChunkReload(() => import("./pages/WorkmateRegistryList").then(m => ({ default: m.WorkmateRegistryList })));
const WorkmateRegistryCreate = lazyWithChunkReload(() => import("./pages/WorkmateRegistryCreate").then(m => ({ default: m.WorkmateRegistryCreate })));
const WorkmateRegistryDetail = lazyWithChunkReload(() => import("./pages/WorkmateRegistryDetail").then(m => ({ default: m.WorkmateRegistryDetail })));
const RegistryPrintColResizeDemo = lazyWithChunkReload(() => import("./dev/RegistryPrintColResizeDemo"));
const RegistryPublicSign = lazyWithChunkReload(() => import("./pages/RegistryPublicSign").then(m => ({ default: m.RegistryPublicSign })));
const RegistrySignComplete = lazyWithChunkReload(() => import("./pages/RegistrySignComplete").then(m => ({ default: m.RegistrySignComplete })));
const BookRequests = lazyWithChunkReload(() => import("./features/book-requests/BookRequests"));
const BookRequestsAccess = lazyWithChunkReload(() => import("./features/book-requests/BookRequestsAccess"));
const BookRequestAdmin = lazyWithChunkReload(() => import("./features/book-requests/BookRequestAdmin"));
const PublicBookRequest = lazyWithChunkReload(() => import("./features/book-requests/PublicBookRequest"));
const WorkmateLostFoundCenters = lazyWithChunkReload(() => import("./pages/WorkmateLostFoundCenters").then(m => ({ default: m.WorkmateLostFoundCenters })));
const WorkmateLostFoundCenterDetail = lazyWithChunkReload(() => import("./pages/WorkmateLostFoundCenterDetail").then(m => ({ default: m.WorkmateLostFoundCenterDetail })));
const LostFoundPublicCenter = lazyWithChunkReload(() => import("./pages/LostFoundPublicCenter").then(m => ({ default: m.LostFoundPublicCenter })));
const LostFoundPublicCreate = lazyWithChunkReload(() => import("./pages/LostFoundPublicCreate").then(m => ({ default: m.LostFoundPublicCreate })));

// Equipment Loan (물품 대여 — 격리된 신규 기능)
const WorkmateEquipmentCenters = lazyWithChunkReload(() => import("./pages/WorkmateEquipmentCenters").then(m => ({ default: m.WorkmateEquipmentCenters })));
const WorkmateEquipmentCenterDetail = lazyWithChunkReload(() => import("./pages/WorkmateEquipmentCenterDetail").then(m => ({ default: m.WorkmateEquipmentCenterDetail })));
const EquipmentPublicCenter = lazyWithChunkReload(() => import("./pages/EquipmentPublicCenter").then(m => ({ default: m.EquipmentPublicCenter })));

// Consent Form pages
const ConsentFormList = lazyWithChunkReload(() => import("./pages/ConsentFormList").then(m => ({ default: m.ConsentFormList })));
const ConsentFormCreate = lazyWithChunkReload(() => import("./pages/ConsentFormCreate").then(m => ({ default: m.ConsentFormCreate })));
const ConsentFormDetail = lazyWithChunkReload(() => import("./pages/ConsentFormDetail").then(m => ({ default: m.ConsentFormDetail })));
const ConsentFormResults = lazyWithChunkReload(() => import("./pages/ConsentFormResults").then(m => ({ default: m.ConsentFormResults })));
const ConsentFormPublicFill = lazyWithChunkReload(() => import("./pages/ConsentFormPublicFill").then(m => ({ default: m.ConsentFormPublicFill })));
const DocumentSignList = lazyWithChunkReload(() => import("./pages/DocumentSignList").then(m => ({ default: m.DocumentSignList })));
const DocumentSignCreate = lazyWithChunkReload(() => import("./pages/DocumentSignCreate").then(m => ({ default: m.DocumentSignCreate })));
const DocumentSignDetail = lazyWithChunkReload(() => import("./pages/DocumentSignDetail").then(m => ({ default: m.DocumentSignDetail })));
const DocumentSignPublicSign = lazyWithChunkReload(() => import("./pages/DocumentSignPublicSign").then(m => ({ default: m.DocumentSignPublicSign })));
const CollectDashboardListPage = lazyWithChunkReload(() =>
  import("./pages/workmate/collect/CollectDashboardListPage").then((m) => ({ default: m.CollectDashboardListPage }))
);
const CollectDashboardFormPage = lazyWithChunkReload(() =>
  import("./pages/workmate/collect/CollectDashboardFormPage").then((m) => ({ default: m.CollectDashboardFormPage }))
);
const CollectPublicForm = lazyWithChunkReload(() =>
  import("./pages/workmate/collect/CollectPublicForm").then((m) => ({ default: m.CollectPublicForm }))
);

// AI 파일 수합
const FileInboxListPage = lazyWithChunkReload(() =>
  import("./pages/workmate/file-inbox/FileInboxListPage").then((m) => ({ default: m.FileInboxListPage }))
);
const FileInboxDetailPage = lazyWithChunkReload(() =>
  import("./pages/workmate/file-inbox/FileInboxDetailPage").then((m) => ({ default: m.FileInboxDetailPage }))
);
const FileInboxPublicPage = lazyWithChunkReload(() =>
  import("./pages/workmate/file-inbox/FileInboxPublicPage").then((m) => ({ default: m.FileInboxPublicPage }))
);

// Training Certs (연수 이수증 수합기 — 격리된 신규 기능)
const TrainingCertsCampaigns = lazyWithChunkReload(() =>
  import("./pages/workmate/training-certs/TrainingCertsCampaigns").then((m) => ({ default: m.TrainingCertsCampaigns }))
);
const TrainingCertsDashboard = lazyWithChunkReload(() =>
  import("./pages/workmate/training-certs/TrainingCertsDashboard").then((m) => ({ default: m.TrainingCertsDashboard }))
);
const TrainingCertsPublicUpload = lazyWithChunkReload(() =>
  import("./pages/workmate/training-certs/TrainingCertsPublicUpload").then((m) => ({ default: m.TrainingCertsPublicUpload }))
);
const WorkmateHwpxFill = lazyWithChunkReload(() =>
  import("./pages/workmate/hwpx-fill/WorkmateHwpxFill").then((m) => ({ default: m.WorkmateHwpxFill }))
);

// Data Lookup (개별 데이터 안심 조회 — 격리된 신규 기능)
const DataLookupList = lazyWithChunkReload(() =>
  import("./pages/workmate/data-lookup/DataLookupList").then((m) => ({ default: m.DataLookupList }))
);
const DataLookupDetail = lazyWithChunkReload(() =>
  import("./pages/workmate/data-lookup/DataLookupDetail").then((m) => ({ default: m.DataLookupDetail }))
);
const DataLookupStudent = lazyWithChunkReload(() =>
  import("./pages/DataLookupStudent").then((m) => ({ default: m.DataLookupStudent }))
);
const DataLookupTokenView = lazyWithChunkReload(() =>
  import("./pages/DataLookupTokenView").then((m) => ({ default: m.DataLookupTokenView }))
);

// Exam Supervision (시험 감독시간표 — 격리 신규 기능)
const ExamSupervisionList = lazyWithChunkReload(() =>
  import("./pages/workmate/exam-supervision/ExamSupervisionList").then((m) => ({ default: m.ExamSupervisionList }))
);
const ExamSupervisionDetail = lazyWithChunkReload(() =>
  import("./pages/workmate/exam-supervision/ExamSupervisionDetail").then((m) => ({ default: m.ExamSupervisionDetail }))
);
const ExamSupervisionPublicView = lazyWithChunkReload(() =>
  import("./pages/workmate/exam-supervision/ExamSupervisionPublicView").then((m) => ({ default: m.ExamSupervisionPublicView }))
);

// Timetable Manager pages
const TimetableSchools = lazyWithChunkReload(() => import("./pages/TimetableSchools").then(m => ({ default: m.TimetableSchools })));
const TimetableAdminSchools = lazyWithChunkReload(() => import("./pages/TimetableAdminSchools").then(m => ({ default: m.TimetableAdminSchools })));
const TimetableSchoolDashboard = lazyWithChunkReload(() => import("./pages/TimetableSchoolDashboard").then(m => ({ default: m.TimetableSchoolDashboard })));
const TimetableOnboarding = lazyWithChunkReload(() => import("./pages/TimetableOnboarding").then(m => ({ default: m.TimetableOnboarding })));
const TimetableImport = lazyWithChunkReload(() => import("./pages/TimetableImport").then(m => ({ default: m.TimetableImport })));
const TimetableSetup = lazyWithChunkReload(() => import("./pages/TimetableSetup").then(m => ({ default: m.TimetableSetup })));
const TimetableCurriculum = lazyWithChunkReload(() => import("./pages/TimetableCurriculum").then(m => ({ default: m.TimetableCurriculum })));
const TimetableConstraints = lazyWithChunkReload(() => import("./pages/TimetableConstraints").then(m => ({ default: m.TimetableConstraints })));
const TimetableGenerate = lazyWithChunkReload(() => import("./pages/TimetableGenerate").then(m => ({ default: m.TimetableGenerate })));
const TimetableView = lazyWithChunkReload(() => import("./pages/TimetableView").then(m => ({ default: m.TimetableView })));
const TimetableTeacherJoin = lazyWithChunkReload(() => import("./pages/TimetableTeacherJoin").then(m => ({ default: m.TimetableTeacherJoin })));
const TimetableInvitation = lazyWithChunkReload(() => import("./pages/TimetableInvitation").then(m => ({ default: m.TimetableInvitation })));
const TimetablePersonal = lazyWithChunkReload(() => import("./pages/TimetablePersonal").then(m => ({ default: m.TimetablePersonal })));
const TimetableWeekly = lazyWithChunkReload(() => import("./pages/TimetableWeekly").then(m => ({ default: m.TimetableWeekly })));
const TimetableShare = lazyWithChunkReload(() => import("./pages/TimetableShare").then(m => ({ default: m.TimetableShare })));
const TimetableSwapRequests = lazyWithChunkReload(() => import("./pages/TimetableSwapRequests").then(m => ({ default: m.TimetableSwapRequests })));
const TimetablePublicView = lazyWithChunkReload(() => import("./pages/TimetablePublicView").then(m => ({ default: m.TimetablePublicView })));
const TimetablePublicWeekly = lazyWithChunkReload(() => import("./pages/TimetablePublicWeekly").then(m => ({ default: m.TimetablePublicWeekly })));
const TimetableIntro = lazyWithChunkReload(() => import("./pages/TimetableIntro").then(m => ({ default: m.TimetableIntro })));

// Informate pages
const AskSchoolQuestion = lazyWithChunkReload(() => import("./pages/SchoolQuestions").then(m => ({ default: m.AskSchoolQuestion })));
const SchoolQuestionInbox = lazyWithChunkReload(() => import("./pages/SchoolQuestions").then(m => ({ default: m.SchoolQuestionInbox })));
const SchoolQuestionDetail = lazyWithChunkReload(() => import("./pages/SchoolQuestions").then(m => ({ default: m.SchoolQuestionDetail })));
const InformateSchoolPreview = lazyWithChunkReload(() => import("./pages/InformateSchoolPreview").then(m => ({ default: m.InformateSchoolPreview })));
const InformateHub = lazyWithChunkReload(() => import("./pages/InformateHub").then(m => ({ default: m.InformateHub })));
const InformateBooks = lazyWithChunkReload(() => import("./pages/InformateBooks").then(m => ({ default: m.InformateBooks })));
const InformateBookDetail = lazyWithChunkReload(() => import("./pages/InformateBookDetail").then(m => ({ default: m.InformateBookDetail })));
const InformateBookSubmit = lazyWithChunkReload(() => import("./pages/InformateBookSubmit").then(m => ({ default: m.InformateBookSubmit })));
const InformateYoutubers = lazyWithChunkReload(() => import("./pages/InformateYoutubers").then(m => ({ default: m.InformateYoutubers })));
const InformateYoutuberDetail = lazyWithChunkReload(() => import("./pages/InformateYoutuberDetail").then(m => ({ default: m.InformateYoutuberDetail })));
const InformateYoutuberSubmit = lazyWithChunkReload(() => import("./pages/InformateYoutuberSubmit").then(m => ({ default: m.InformateYoutuberSubmit })));
const InformateBlogers = lazyWithChunkReload(() => import("./pages/InformateBlogers").then(m => ({ default: m.InformateBlogers })));
const InformateBlogerSubmit = lazyWithChunkReload(() => import("./pages/InformateBlogerSubmit").then(m => ({ default: m.InformateBlogerSubmit })));
const InformateMbti = lazyWithChunkReload(() => import("./pages/InformateMbti").then(m => ({ default: m.InformateMbti })));
const InformateOpenChats = lazyWithChunkReload(() => import("./pages/InformateOpenChats").then(m => ({ default: m.InformateOpenChats })));
const InformateVibeSites = lazyWithChunkReload(() => import("./pages/InformateVibeSites").then(m => ({ default: m.InformateVibeSites })));
const InformateVibeSiteBrowse = lazyWithChunkReload(() => import("./pages/InformateVibeSiteBrowse").then(m => ({ default: m.InformateVibeSiteBrowse })));
const InformateVibeSiteForm = lazyWithChunkReload(() => import("./pages/InformateVibeSiteForm").then(m => ({ default: m.InformateVibeSiteForm })));

// Classmate pages
const ClassmateHub = lazyWithChunkReload(() => import("./pages/ClassmateHub").then(m => ({ default: m.ClassmateHub })));
const ClassmateClassrooms = lazyWithChunkReload(() => import("./pages/ClassmateClassrooms").then(m => ({ default: m.ClassmateClassrooms })));
const ClassmateClassroomDetail = lazyWithChunkReload(() => import("./pages/ClassmateClassroomDetail").then(m => ({ default: m.ClassmateClassroomDetail })));
const ClassmateArrangements = lazyWithChunkReload(() => import("./pages/ClassmateArrangements").then(m => ({ default: m.ClassmateArrangements })));
const ClassmateArrangementDetail = lazyWithChunkReload(() => import("./pages/ClassmateArrangementDetail").then(m => ({ default: m.ClassmateArrangementDetail })));
const ClassmateActivities = lazyWithChunkReload(() => import("./pages/ClassmateActivities").then(m => ({ default: m.ClassmateActivities })));
const ClassmateRandomPick = lazyWithChunkReload(() => import("./pages/ClassmateRandomPick").then(m => ({ default: m.ClassmateRandomPick })));
const ClassmateRandomPickLibrary = lazyWithChunkReload(() => import("./pages/ClassmateRandomPickLibrary").then(m => ({ default: m.ClassmateRandomPickLibrary })));
const ClassmateRandomPickSetEditor = lazyWithChunkReload(() => import("./pages/ClassmateRandomPickSetEditor").then(m => ({ default: m.ClassmateRandomPickSetEditor })));
const ClassmateProgress = lazyWithChunkReload(() => import("./pages/ClassmateProgress").then(m => ({ default: m.ClassmateProgress })));
const ClassmateAttendanceManagement = lazyWithChunkReload(() => import("./pages/ClassmateAttendanceManagement").then(m => ({ default: m.ClassmateAttendanceManagement })));
const ClassmateAttendanceDetail = lazyWithChunkReload(() => import("./pages/ClassmateAttendanceDetail").then(m => ({ default: m.ClassmateAttendanceDetail })));
const ClassmateAttendanceRegister = lazyWithChunkReload(() => import("./pages/ClassmateAttendanceRegister").then(m => ({ default: m.ClassmateAttendanceRegister })));
const ClassmateProgressDetail = lazyWithChunkReload(() => import("./pages/ClassmateProgressDetail").then(m => ({ default: m.ClassmateProgressDetail })));
const ClassmateAttendance = lazyWithChunkReload(() => import("./pages/ClassmateAttendance").then(m => ({ default: m.ClassmateAttendance })));
const ClassmateVotes = lazyWithChunkReload(() => import("./pages/ClassmateVotes").then(m => ({ default: m.ClassmateVotes })));
const ClassmateKeyboardPlay = lazyWithChunkReload(() => import("./pages/ClassmateKeyboardPlay").then(m => ({ default: m.ClassmateKeyboardPlay })));
const ClassmateVoteScreen = lazyWithChunkReload(() => import("./pages/ClassmateVoteScreen").then(m => ({ default: m.ClassmateVoteScreen })));
const ClassmateVoteResultScreen = lazyWithChunkReload(() => import("./pages/ClassmateVoteResultScreen").then(m => ({ default: m.ClassmateVoteResultScreen })));
const ClassmateGames = lazyWithChunkReload(() => import("./pages/ClassmateGames").then(m => ({ default: m.ClassmateGames })));
const ClassmateGameCreate = lazyWithChunkReload(() => import("./pages/ClassmateGameCreate").then(m => ({ default: m.ClassmateGameCreate })));
const ClassmateGameDetail = lazyWithChunkReload(() => import("./pages/ClassmateGameDetail").then(m => ({ default: m.ClassmateGameDetail })));
const ClassmateGameEdit = lazyWithChunkReload(() => import("./pages/ClassmateGameEdit").then(m => ({ default: m.ClassmateGameEdit })));
const ClassmateGamePlay = lazyWithChunkReload(() => import("./pages/ClassmateGamePlay").then(m => ({ default: m.ClassmateGamePlay })));
const QuizStudioSetup = lazyWithChunkReload(() => import("./pages/QuizStudioSetup").then(m => ({ default: m.QuizStudioSetup })));
const QuizBattleGames = lazyWithChunkReload(() => import("./pages/QuizBattleGames").then(m => ({ default: m.QuizBattleGames })));
const QuizBattleCreate = lazyWithChunkReload(() => import("./pages/QuizBattleCreate").then(m => ({ default: m.QuizBattleCreate })));
const QuizBattleDetail = lazyWithChunkReload(() => import("./pages/QuizBattleDetail").then(m => ({ default: m.QuizBattleDetail })));
const QuizBattleEdit = lazyWithChunkReload(() => import("./pages/QuizBattleEdit").then(m => ({ default: m.QuizBattleEdit })));
const QuizBattlePlay = lazyWithChunkReload(() => import("./pages/QuizBattlePlay").then(m => ({ default: m.QuizBattlePlay })));
const QuizBattlePlay4Team = lazyWithChunkReload(() => import("./pages/QuizBattlePlay4Team").then(m => ({ default: m.QuizBattlePlay4Team })));
const QuizBattlePlayZombie = lazyWithChunkReload(() => import("./pages/QuizBattlePlayZombie").then(m => ({ default: m.QuizBattlePlayZombie })));
const QuizBattlePlayArcade = lazyWithChunkReload(() => import("./pages/QuizBattlePlayArcade").then(m => ({ default: m.QuizBattlePlayArcade })));
const QuizBattlePlayCardFlip = lazyWithChunkReload(() => import("./pages/QuizBattlePlayCardFlip").then(m => ({ default: m.QuizBattlePlayCardFlip })));
const QuizBattleLiveJoin = lazyWithChunkReload(() =>
  import("./pages/QuizBattleLiveJoin").then((m) => ({ default: m.QuizBattleLiveJoin }))
);
const QuizBattleLiveDashboard = lazyWithChunkReload(() =>
  import("./pages/QuizBattleLiveDashboard").then((m) => ({ default: m.QuizBattleLiveDashboard }))
);
const QuizBattleLiveSessionResult = lazyWithChunkReload(() =>
  import("./pages/QuizBattleLiveSessionResult").then((m) => ({ default: m.QuizBattleLiveSessionResult }))
);
const PeerQuizRoomList = lazyWithChunkReload(() =>
  import("./pages/PeerQuizRoomList").then((m) => ({ default: m.PeerQuizRoomList }))
);
const PeerQuizHostDashboard = lazyWithChunkReload(() =>
  import("./pages/PeerQuizHostDashboard").then((m) => ({ default: m.PeerQuizHostDashboard }))
);
const PeerQuizJoin = lazyWithChunkReload(() =>
  import("./pages/PeerQuizJoin").then((m) => ({ default: m.PeerQuizJoin }))
);
const PeerQuizRoundDetail = lazyWithChunkReload(() =>
  import("./pages/PeerQuizRoundDetail").then((m) => ({ default: m.PeerQuizRoundDetail }))
);
const ClassmateQr = lazyWithChunkReload(() => import("./pages/ClassmateQr").then(m => ({ default: m.ClassmateQr })));
const ClassmateTimer = lazyWithChunkReload(() => import("./pages/ClassmateTimer").then(m => ({ default: m.ClassmateTimer })));
const ClassmateBoard = lazyWithChunkReload(() =>
  import("./pages/ClassmateBoard").then((m) => ({ default: m.ClassmateBoard })),
);
const ClassmateSociometryIntro = lazyWithChunkReload(() => import("./pages/ClassmateSociometryIntro").then(m => ({ default: m.ClassmateSociometryIntro })));
const ClassmateSociometry = lazyWithChunkReload(() => import("./pages/ClassmateSociometry").then(m => ({ default: m.ClassmateSociometry })));
const ClassmateSociometryDashboard = lazyWithChunkReload(() => import("./pages/ClassmateSociometryDashboard").then(m => ({ default: m.ClassmateSociometryDashboard })));
const ClassmateSociometryPublicSurvey = lazyWithChunkReload(() => import("./pages/ClassmateSociometryPublicSurvey").then(m => ({ default: m.ClassmateSociometryPublicSurvey })));
const ClassmateAlbumList = lazyWithChunkReload(() => import("./pages/ClassmateAlbumList").then(m => ({ default: m.ClassmateAlbumList })));
const ClassmateAlbumRoom = lazyWithChunkReload(() => import("./features/album/AlbumRoom"));
const ClassmateWallList = lazyWithChunkReload(() => import("./pages/ClassmateWallList").then(m => ({ default: m.ClassmateWallList })));
const ClassmateWallRoom = lazyWithChunkReload(() => import("./features/wall/WallRoom"));
const ClassmateWordCloudList = lazyWithChunkReload(() => import("./pages/ClassmateWordCloudList").then(m => ({ default: m.ClassmateWordCloudList })));
const ClassmateWordCloudDashboard = lazyWithChunkReload(() => import("./pages/ClassmateWordCloudDashboard").then(m => ({ default: m.ClassmateWordCloudDashboard })));
const ClassmateWordCloudParticipant = lazyWithChunkReload(() => import("./pages/ClassmateWordCloudParticipant").then(m => ({ default: m.ClassmateWordCloudParticipant })));

// Reading Assessment (스피치 발음 평가 — 격리된 신규 기능)
const ClassmateReadingAssessmentList = lazyWithChunkReload(() =>
  import("./pages/ClassmateReadingAssessmentList").then((m) => ({
    default: m.ClassmateReadingAssessmentList,
  }))
);
const ClassmateReadingAssessmentDashboard = lazyWithChunkReload(() =>
  import("./pages/ClassmateReadingAssessmentDashboard").then((m) => ({
    default: m.ClassmateReadingAssessmentDashboard,
  }))
);
const ClassmateReadingAssessmentStudent = lazyWithChunkReload(() =>
  import("./pages/ClassmateReadingAssessmentStudent").then((m) => ({
    default: m.ClassmateReadingAssessmentStudent,
  }))
);

// Essay Grading (논술형 평가 — 격리된 신규 기능)
const ClassmateEssayGradingList = lazyWithChunkReload(() =>
  import("./pages/ClassmateEssayGradingList").then((m) => ({
    default: m.ClassmateEssayGradingList,
  }))
);
const ClassmateEssayGradingDetail = lazyWithChunkReload(() =>
  import("./pages/ClassmateEssayGradingDetail").then((m) => ({
    default: m.ClassmateEssayGradingDetail,
  }))
);

// 발표 버저
const ClassmateBuzzerList = lazyWithChunkReload(() => import("./pages/ClassmateBuzzer").then(m => ({ default: m.ClassmateBuzzerList })));
const ClassmateBuzzerHost = lazyWithChunkReload(() => import("./pages/ClassmateBuzzer").then(m => ({ default: m.ClassmateBuzzerHost })));
const ClassmateBuzzerJoin = lazyWithChunkReload(() => import("./pages/ClassmateBuzzerJoin").then(m => ({ default: m.ClassmateBuzzerJoin })));
// OnePick (원픽 — 격리된 신규 기능)
const ClassmateOnepickList = lazyWithChunkReload(() => import("./pages/ClassmateOnepickList").then(m => ({ default: m.ClassmateOnepickList })));
const ClassmateOnepickDashboard = lazyWithChunkReload(() => import("./pages/ClassmateOnepickDashboard").then(m => ({ default: m.ClassmateOnepickDashboard })));
const ClassmateOnepickParticipant = lazyWithChunkReload(() => import("./pages/ClassmateOnepickParticipant").then(m => ({ default: m.ClassmateOnepickParticipant })));

// Group Match (모둠 매칭 — 격리된 신규 기능)
const ClassmateGroupMatchList = lazyWithChunkReload(() =>
  import("./pages/ClassmateGroupMatchList").then((m) => ({ default: m.ClassmateGroupMatchList }))
);
const ClassmateGroupMatchDetail = lazyWithChunkReload(() =>
  import("./pages/ClassmateGroupMatchDetail").then((m) => ({ default: m.ClassmateGroupMatchDetail }))
);
const ClassmateGroupMatchJoin = lazyWithChunkReload(() =>
  import("./pages/ClassmateGroupMatchJoin").then((m) => ({ default: m.ClassmateGroupMatchJoin }))
);

// 오답노트 생성기 (격리된 신규 기능)
const ClassmateWrongAnswerNote = lazyWithChunkReload(() =>
  import("./pages/ClassmateWrongAnswerNote").then((m) => ({ default: m.ClassmateWrongAnswerNote }))
);

// Secret 16 (시크릿 16 — 격리된 신규 기능)
const ClassmateSecret16Programs = lazyWithChunkReload(() =>
  import("./pages/ClassmateSecret16Programs").then((m) => ({ default: m.ClassmateSecret16Programs }))
);
const ClassmateSecret16ProgramEditor = lazyWithChunkReload(() =>
  import("./pages/ClassmateSecret16ProgramEditor").then((m) => ({
    default: m.ClassmateSecret16ProgramEditor,
  }))
);
const ClassmateSecret16ProgramHub = lazyWithChunkReload(() =>
  import("./pages/ClassmateSecret16ProgramHub").then((m) => ({ default: m.ClassmateSecret16ProgramHub }))
);
const ClassmateSecret16HostDashboard = lazyWithChunkReload(() =>
  import("./pages/ClassmateSecret16HostDashboard").then((m) => ({ default: m.ClassmateSecret16HostDashboard }))
);
const ClassmateSecret16Join = lazyWithChunkReload(() =>
  import("./pages/ClassmateSecret16Join").then((m) => ({ default: m.ClassmateSecret16Join }))
);
const ClassmateSecret16Game = lazyWithChunkReload(() =>
  import("./pages/ClassmateSecret16Game").then((m) => ({ default: m.ClassmateSecret16Game }))
);
const ClassmateSecret16Result = lazyWithChunkReload(() =>
  import("./pages/ClassmateSecret16Result").then((m) => ({ default: m.ClassmateSecret16Result }))
);

// Community pages (최적화 대상)
const CommunityBoard = lazyWithChunkReload(() => import("./pages/CommunityBoard").then(m => ({ default: m.CommunityBoard })));
const CommunityPostForm = lazyWithChunkReload(() => import("./pages/CommunityPostForm").then(m => ({ default: m.CommunityPostForm })));
const CommunityPostDetailPage = lazyWithChunkReload(() => import("./pages/CommunityPostDetail").then(m => ({ default: m.CommunityPostDetailPage })));
const CommunityMyActivity = lazyWithChunkReload(() => import("./pages/CommunityMyActivity").then(m => ({ default: m.CommunityMyActivity })));

// Admin pages
const AdminMailingList = lazyWithChunkReload(() => import("./pages/AdminMailingList").then(m => ({ default: m.AdminMailingList })));
const AdminCreditGrant = lazyWithChunkReload(() => import("./pages/AdminCreditGrant").then(m => ({ default: m.AdminCreditGrant })));
const AdminPointGrant = lazyWithChunkReload(() => import("./pages/AdminPointGrant").then(m => ({ default: m.AdminPointGrant })));
const AdminSupabaseUsers = lazyWithChunkReload(() => import("./pages/AdminSupabaseUsers").then(m => ({ default: m.AdminSupabaseUsers })));
const AdminToolStats = lazyWithChunkReload(() => import("./pages/AdminToolStats").then(m => ({ default: m.AdminToolStats })));
const AdminPointDistribution = lazyWithChunkReload(() => import("./pages/AdminPointDistribution").then(m => ({ default: m.AdminPointDistribution })));
const AdminHomeHeroAdPage = lazyWithChunkReload(() => import("./pages/AdminHomeHeroAd").then(m => ({ default: m.AdminHomeHeroAdPage })));
const AdminCreditBankOrdersPage = lazyWithChunkReload(() => import("./pages/AdminCreditBankOrders").then(m => ({ default: m.AdminCreditBankOrdersPage })));
const UnsubscribePage = lazyWithChunkReload(() => import("./pages/UnsubscribePage").then(m => ({ default: m.UnsubscribePage })));

// Office Builder pages (격리된 신규 기능)
const OfficeBuilderListPage = lazyWithChunkReload(() => import("./features/office-builder/pages/OfficeBuilderListPage").then(m => ({ default: m.OfficeBuilderListPage })));
const OfficeBuilderEditorPage = lazyWithChunkReload(() => import("./features/office-builder/pages/OfficeBuilderEditorPage").then(m => ({ default: m.OfficeBuilderEditorPage })));
const OfficeBuilderPublicPage = lazyWithChunkReload(() => import("./features/office-builder/pages/OfficeBuilderPublicPage").then(m => ({ default: m.OfficeBuilderPublicPage })));

const QUIZ_LIVE_JOIN_PATH_PREFIX = "/classmate/quiz-battle/live/join/";
const QUIZ_LIVE_JOIN_RECOVERY_KEY = "quiz-live-join-route-recovery-v1";

function isQuizLiveJoinPath(pathname: string): boolean {
  return pathname.startsWith(QUIZ_LIVE_JOIN_PATH_PREFIX) && pathname.length > QUIZ_LIVE_JOIN_PATH_PREFIX.length;
}

function StaleRouteRecovery() {
  const location = useLocation();
  const [didAutoRetry, setDidAutoRetry] = useState(false);
  const isLiveJoinPath = isQuizLiveJoinPath(location.pathname);

  useEffect(() => {
    if (!isLiveJoinPath) {
      try {
        sessionStorage.removeItem(QUIZ_LIVE_JOIN_RECOVERY_KEY);
      } catch {
        /* ignore storage errors */
      }
      setDidAutoRetry(false);
      return;
    }

    let hasRetried = false;
    try {
      const recoveredPath = sessionStorage.getItem(QUIZ_LIVE_JOIN_RECOVERY_KEY);
      hasRetried = recoveredPath === location.pathname;
      if (!hasRetried) {
        sessionStorage.setItem(QUIZ_LIVE_JOIN_RECOVERY_KEY, location.pathname);
      }
    } catch {
      // If storage is blocked, show recovery controls instead of reloading forever.
      hasRetried = true;
    }

    if (!hasRetried) {
      window.location.replace(`${location.pathname}${location.search}${location.hash}`);
      return;
    }

    setDidAutoRetry(true);
  }, [isLiveJoinPath, location.hash, location.pathname, location.search]);

  if (!isLiveJoinPath) {
    return <Navigate to="/" replace />;
  }

  if (!didAutoRetry) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 p-6">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-slate-900/80 p-6 text-center text-slate-100">
        <h1 className="text-xl font-black">접속을 다시 시도해 주세요</h1>
        <p className="mt-3 text-sm text-slate-300 break-keep">
          링크 접속을 자동으로 한 번 복구했지만 페이지를 열지 못했어요. 아래 버튼으로 다시 시도하거나 홈으로 이동해 주세요.
        </p>
        <div className="mt-5 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => {
              try {
                sessionStorage.removeItem(QUIZ_LIVE_JOIN_RECOVERY_KEY);
              } catch {
                /* ignore storage errors */
              }
              window.location.replace(`${location.pathname}${location.search}${location.hash}`);
            }}
            className="rounded-xl bg-emerald-500 px-4 py-2 text-sm font-bold text-slate-950"
          >
            다시 시도
          </button>
          <button
            type="button"
            onClick={() => {
              try {
                sessionStorage.removeItem(QUIZ_LIVE_JOIN_RECOVERY_KEY);
              } catch {
                /* ignore storage errors */
              }
              window.location.replace("/");
            }}
            className="rounded-xl border border-white/20 px-4 py-2 text-sm font-semibold text-slate-100"
          >
            홈으로
          </button>
        </div>
      </div>
    </div>
  );
}

function TimetableSchoolsRoute() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <PageLoading message="로그인 상태를 확인하고 있어요" />;
  return isAuthenticated ? <TimetableSchools /> : <TimetableIntro />;
}

function SociometryRoute() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <PageLoading message="로그인 상태를 확인하고 있어요" />;
  return isAuthenticated ? <ClassmateSociometry /> : <ClassmateSociometryIntro />;
}

export default function App() {
  const location = useLocation();
  const background = /^\/informate\/schools\/questions\/[^/]+$/.test(location.pathname)
    ? (location.state?.schoolQuestionBackground as Location | undefined) : undefined;
  return (
    <>
      <ScrollToTop />
      <PageErrorBoundary>
      {/* Keep route transitions free of an intermediate loading screen. */}
      <Suspense fallback={null}>
        <Routes>
          <Route path="/s" element={<StudentActivityPortal />} />
          <Route path="/s/r/:token" element={<PublicActivityRanking />} />
          {/* QuizBattlePlay: 헤더/푸터 없이 전체 화면으로 렌더링 */}
          <Route path="/classmate/quiz-battle/:setId/play" element={<QuizBattlePlay />} />
          <Route path="/classmate/quiz-battle/:setId/play-4team" element={<QuizBattlePlay4Team />} />
          <Route path="/classmate/quiz-battle/:setId/play-zombie" element={<QuizBattlePlayZombie />} />
          <Route path="/classmate/quiz-battle/:setId/play-arcade" element={<QuizBattlePlayArcade />} />
          <Route path="/classmate/quiz-battle/:setId/play-cardflip" element={<QuizBattlePlayCardFlip />} />

          {/* 게임 시간 쟁탈 학생 참여 (로그인 불필요, fullscreen) */}
          <Route path="/classmate/quiz-battle/live/join/:joinCode" element={<QuizBattleLiveJoin />} />

          {/* 학생 문제 배틀 (로그인 불필요, fullscreen) */}
          <Route path="/classmate/peer-quiz/join/:joinCode" element={<PeerQuizJoin />} />

          {/* Timetable Public Views (No AppShell layout) */}
          <Route path="/timetable/public/weekly/:token" element={<TimetablePublicWeekly />} />
          <Route path="/timetable/public/:token" element={<TimetablePublicView />} />

          {/* Registry Public View */}
          <Route path="/registry/sign/:id" element={<RegistryPublicSign />} />
          <Route path="/registry/sign/:id/complete" element={<RegistrySignComplete />} />

          {/* Consent Form Public Fill */}
          <Route path="/consent-form/fill/:id" element={<ConsentFormPublicFill />} />
          <Route path="/document-sign/sign/:token" element={<DocumentSignPublicSign />} />
          <Route path="/book-request/:token" element={<PublicBookRequest />} />
          <Route path="/lost-found/:slug" element={<LostFoundPublicCenter />} />
          <Route path="/lost-found/:slug/new" element={<LostFoundPublicCreate />} />

          {/* Equipment Loan: 공개 대여 페이지 (No AppShell, 로그인 불필요) */}
          <Route path="/equipment-loan/:slug" element={<EquipmentPublicCenter />} />

          {/* Classmate: 키보드 투표 전체 화면 모드 (No AppShell layout) */}
          <Route path="/classmate/votes/play" element={<ClassmateKeyboardPlay />} />

          {/* Sociometry: 학생용 설문 페이지 (공개, No AppShell) */}
          <Route path="/classmate/sociometry/s/:surveyId/:studentId" element={<ClassmateSociometryPublicSurvey />} />

          {/* Office Builder: 공개 뷰어 (No AppShell) */}
          <Route path="/p/:slug" element={<OfficeBuilderPublicPage />} />

          {/* Word Cloud Participant: 학생 참여 (No AppShell) */}
          <Route path="/classmate/albums/:id" element={<ClassmateAlbumRoom />} />
          <Route path="/classmate/albums/:id/join" element={<ClassmateAlbumRoom />} />
          <Route path="/classmate/walls/:id" element={<ClassmateWallRoom />} />
          <Route path="/classmate/walls/:id/join" element={<ClassmateWallRoom />} />
          <Route path="/classmate/wordcloud/:id/join" element={<ClassmateWordCloudParticipant />} />

          {/* Reading Assessment: 학생 스피치 녹음 (No AppShell) */}
          <Route
            path="/classmate/reading-assessment/r/:slug"
            element={<ClassmateReadingAssessmentStudent />}
          />

          {/* 학생 발표 참여 (No AppShell, 로그인 불필요) */}
          <Route path="/classmate/buzzer/:id/join" element={<ClassmateBuzzerJoin />} />
          {/* OnePick Participant */}
          <Route path="/classmate/onepick/:id/vote" element={<ClassmateOnepickParticipant />} />

          {/* Group Match: 응답자 참여 (No AppShell) */}
          <Route path="/classmate/group-match/join/:joinCode" element={<ClassmateGroupMatchJoin />} />

          {/* Secret 16: 학생 참여·게임 (No AppShell) */}
          <Route path="/classmate/secret16/join/:joinCode" element={<ClassmateSecret16Join />} />
          <Route path="/classmate/secret16/play/:joinCode" element={<ClassmateSecret16Game />} />
          <Route path="/classmate/secret16/result/:joinCode" element={<ClassmateSecret16Result />} />

          {/* Workmate Collect: 공개 제출·타임라인 (No AppShell — 독립 랜딩) */}
          <Route path="/workmate/collect/p/:formId" element={<CollectPublicForm />} />

          {/* AI 파일 수합: 공개 업로드 (No AppShell) */}
          <Route path="/workmate/file-inbox/u/:token" element={<FileInboxPublicPage />} />

          {/* Training Certs: 공개 업로드 (No AppShell, 로그인 불필요) */}
          <Route path="/workmate/training-certs/upload/:token" element={<TrainingCertsPublicUpload />} />

          {/* Data Lookup: 학생 조회 (No AppShell, 로그인 불필요) */}
          <Route path="/lookup/:slug" element={<DataLookupStudent />} />
          <Route path="/lookup/v/:token" element={<DataLookupTokenView />} />

          {/* Exam Supervision Public View */}
          <Route path="/exam-supervision/p/:token" element={<ExamSupervisionPublicView />} />

          {/* 구독 취소: 이메일 링크에서 직접 접근 (No AppShell, 로그인 불필요) */}
          <Route path="/unsubscribe/:uid/:groupId" element={<UnsubscribePage />} />

          {/* 나머지 페이지들: AppShell로 감싸서 헤더/푸터 포함 */}
          <Route path="*" element={
            <AppShell>
              <Routes location={background || location}>
                <Route path="/" element={<Dashboard />} />
                <Route path="/login" element={<Login />} />
                <Route path="/login/supabase-callback" element={<SupabaseAuthCallback />} />
                <Route path="/update-password" element={<UpdatePassword />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/guide/*" element={<Guide />} />
                {import.meta.env.DEV && (
                  <Route
                    path="/dev/registry-print-col-resize"
                    element={
                      <Suspense fallback={null}>
                        <RegistryPrintColResizeDemo />
                      </Suspense>
                    }
                  />
                )}
                <Route path="/workmate" element={<WorkmateHub />} />
                <Route path="/workmate/poster-studio" element={<PosterStudio />} />
                <Route path="/classmate" element={<ClassmateHub />} />
                <Route path="/informate" element={<InformateHub />} />
                <Route path="/timetable/connect/:token" element={<RequireAuth><TimetableTeacherJoin /></RequireAuth>} />
                <Route path="/timetable/invitations/:invitationId" element={<RequireAuth><TimetableInvitation /></RequireAuth>} />
                <Route path="/timetable" element={<Navigate to="/timetable/schools" replace />} />
                <Route
                  path="/timetable/schools"
                  element={
                    <TimetableSchoolsRoute />
                  }
                />
                <Route
                  path="/timetable/admin"
                  element={
                    <RequireAuth>
                      <TimetableAdminSchools />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/timetable/schools/:id"
                  element={
                    <RequireAuth>
                      <TimetableSchoolDashboard />
                    </RequireAuth>
                  }
                />
                <Route path="/timetable/schools/:id/start" element={<RequireAuth><TimetableOnboarding /></RequireAuth>} />
                <Route
                  path="/timetable/schools/:id/import"
                  element={
                    <RequireAuth>
                      <TimetableImport />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/timetable/schools/:id/setup"
                  element={
                    <RequireAuth>
                      <TimetableSetup />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/timetable/schools/:id/curriculum"
                  element={
                    <RequireAuth>
                      <TimetableCurriculum />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/timetable/schools/:id/constraints"
                  element={
                    <RequireAuth>
                      <TimetableConstraints />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/timetable/schools/:id/generate"
                  element={
                    <RequireAuth>
                      <TimetableGenerate />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/timetable/schools/:id/view"
                  element={
                    <RequireAuth>
                      <TimetableView />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/timetable/schools/:id/my"
                  element={<RequireAuth><TimetablePersonal /></RequireAuth>}
                />
                <Route
                  path="/timetable/schools/:id/weekly/view"
                  element={<RequireAuth><TimetablePublicWeekly schoolView /></RequireAuth>}
                />
                <Route
                  path="/timetable/schools/:id/weekly"
                  element={
                    <RequireAuth>
                      <TimetableWeekly />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/timetable/schools/:id/share"
                  element={
                    <RequireAuth>
                      <TimetableShare />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/timetable/schools/:id/requests"
                  element={
                    <RequireAuth>
                      <TimetableSwapRequests />
                    </RequireAuth>
                  }
                />
                <Route path="/community" element={<CommunityBoard />} />
                <Route path="/community/board" element={<CommunityBoard />} />
                <Route
                  path="/community/my"
                  element={
                    <RequireAuth>
                      <CommunityMyActivity />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/community/board/new"
                  element={
                    <RequireAuth>
                      <CommunityPostForm />
                    </RequireAuth>
                  }
                />
                <Route path="/community/board/:postId" element={<CommunityPostDetailPage />} />
                <Route
                  path="/community/board/:postId/edit"
                  element={
                    <RequireAuth>
                      <CommunityPostForm />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin/mailing"
                  element={
                    <RequireAuth>
                      <AdminMailingList />
                    </RequireAuth>
                  }
                />
                <Route path="/admin/credit-grant" element={<RequireAuth><AdminCreditGrant /></RequireAuth>} />
                <Route
                  path="/admin/point-grant"
                  element={
                    <RequireAuth>
                      <AdminPointGrant />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin/supabase-users"
                  element={
                    <RequireAuth>
                      <AdminSupabaseUsers />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin/tool-stats"
                  element={
                    <RequireAuth>
                      <AdminToolStats />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin/point-distribution"
                  element={
                    <RequireAuth>
                      <AdminPointDistribution />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin/home-hero-ad"
                  element={
                    <RequireAuth>
                      <AdminHomeHeroAdPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin/credit-bank"
                  element={
                    <RequireAuth>
                      <AdminCreditBankOrdersPage />
                    </RequireAuth>
                  }
                />
                <Route path="/workmate/student-record" element={<StudentRecord />} />
                <Route path="/workmate/record-studio" element={<Navigate to="/classmate/record-studio" replace />} />
                <Route path="/classmate/record-studio" element={<RecordStudio />} />
                <Route
                  path="/workmate/student-record/history"
                  element={
                    <RequireAuth>
                      <StudentRecordHistory />
                    </RequireAuth>
                  }
                />
                <Route path="/workmate/teacher-jobs" element={<WorkmateTeacherJobs />} />
                <Route path="/workmate/school-documents" element={<WorkmateSchoolDocuments />} />
                <Route
                  path="/workmate/school-conversation"
                  element={
                    <RequireAuth>
                      <WorkmateSchoolConversation />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/school-conversation/questions"
                  element={
                    <RequireAuth>
                      <SchoolQuestionInbox />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/school-conversation/questions/:questionId"
                  element={
                    <RequireAuth>
                      <SchoolQuestionDetail />
                    </RequireAuth>
                  }
                />
                <Route path="/workmate/evaluation-plan" element={<EvaluationPlan />} />
                <Route path="/workmate/exam-score" element={<ExamScore />} />
                <Route path="/workmate/custom-gpt" element={<CustomGptList />} />
                <Route path="/workmate/custom-gpt/create" element={<CustomGptCreate />} />
                <Route path="/workmate/custom-gpt/:id" element={<CustomGptDetail />} />
                <Route path="/workmate/file-converter" element={<WorkmateFileConverter />} />
                <Route path="/workmate/worksheet-restore" element={<WorkmateWorksheetRestore />} />
                <Route path="/workmate/worksheet-restore/question-bank" element={<WorkmateQuestionBank />} />
                <Route path="/workmate/question-bank" element={<Navigate to="/workmate/worksheet-restore/question-bank" replace />} />
                <Route path="/workmate/worksheet-restore/math-diagram" element={<WorkmateMathDiagram />} />
                <Route path="/workmate/math-diagram" element={<Navigate to="/workmate/worksheet-restore/math-diagram" replace />} />
                <Route path="/workmate/purchase-order" element={<WorkmatePurchaseOrder />} />
                <Route
                  path="/workmate/receipts"
                  element={
                    <RequireAuth>
                      <ReceiptsTopics />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/receipts/:topicId"
                  element={
                    <RequireAuth>
                      <ReceiptsTopic />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/receipts/:topicId/upload"
                  element={
                    <RequireAuth>
                      <ReceiptsUpload />
                    </RequireAuth>
                  }
                />
                <Route path="/workmate/re-class" element={<ReClass />} />
                <Route path="/workmate/latex-to-hwp" element={<LatexToHwp />} />
                <Route
                  path="/workmate/timetable"
                  element={
                    <RequireAuth>
                      <WorkmateTimetable />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/essay-grading"
                  element={
                    <RequireAuth>
                      <EssayGradingList />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/essay-grading/:assessmentId"
                  element={
                    <RequireAuth>
                      <EssayGradingDetail />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/registry"
                  element={
                    <RequireAuth>
                      <WorkmateRegistryList />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/registry/create"
                  element={
                    <RequireAuth>
                      <WorkmateRegistryCreate />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/registry/:id"
                  element={
                    <RequireAuth>
                      <WorkmateRegistryDetail />
                    </RequireAuth>
                  }
                />
                <Route path="/workmate/book-requests" element={<BookRequestsAccess><BookRequests /></BookRequestsAccess>} />
                <Route path="/workmate/book-requests/:id" element={<BookRequestsAccess><BookRequestAdmin /></BookRequestsAccess>} />
                <Route
                  path="/workmate/lost-found"
                  element={
                    <RequireAuth>
                      <WorkmateLostFoundCenters />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/lost-found/:id"
                  element={
                    <RequireAuth>
                      <WorkmateLostFoundCenterDetail />
                    </RequireAuth>
                  }
                />

                {/* Equipment Loan — 물품 대여 (격리 신규 기능) */}
                <Route
                  path="/workmate/equipment-loan"
                  element={
                    <RequireAuth>
                      <WorkmateEquipmentCenters />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/equipment-loan/:id"
                  element={
                    <RequireAuth>
                      <WorkmateEquipmentCenterDetail />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/consent-form"
                  element={
                    <RequireAuth>
                      <ConsentFormList />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/consent-form/create"
                  element={
                    <RequireAuth>
                      <ConsentFormCreate />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/consent-form/:id/results"
                  element={
                    <RequireAuth>
                      <ConsentFormResults />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/consent-form/:id"
                  element={
                    <RequireAuth>
                      <ConsentFormDetail />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/document-sign"
                  element={
                    <RequireAuth>
                      <DocumentSignList />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/document-sign/create"
                  element={
                    <RequireAuth>
                      <DocumentSignCreate />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/document-sign/:id"
                  element={
                    <RequireAuth>
                      <DocumentSignDetail />
                    </RequireAuth>
                  }
                />

                <Route
                  path="/workmate/collect/dashboard/:formId"
                  element={
                    <RequireAuth>
                      <CollectDashboardFormPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/collect/dashboard"
                  element={
                    <RequireAuth>
                      <CollectDashboardListPage />
                    </RequireAuth>
                  }
                />

                <Route
                  path="/workmate/file-inbox/:campaignId"
                  element={
                    <RequireAuth>
                      <FileInboxDetailPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/file-inbox"
                  element={
                    <RequireAuth>
                      <FileInboxListPage />
                    </RequireAuth>
                  }
                />

                {/* Training Certs — 연수 이수증 수합기 (격리 신규 기능) */}
                <Route
                  path="/workmate/training-certs"
                  element={
                    <RequireAuth>
                      <TrainingCertsCampaigns />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/training-certs/:campaignId"
                  element={
                    <RequireAuth>
                      <TrainingCertsDashboard />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/hwpx-fill"
                  element={
                    <RequireAuth>
                      <WorkmateHwpxFill />
                    </RequireAuth>
                  }
                />
                {/* Data Lookup — 개별 데이터 안심 조회 (격리 신규 기능) */}
                <Route
                  path="/workmate/data-lookup"
                  element={
                    <RequireAuth>
                      <DataLookupList />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/data-lookup/:eventId"
                  element={
                    <RequireAuth>
                      <DataLookupDetail />
                    </RequireAuth>
                  }
                />
                {/* Exam Supervision — 시험 감독시간표 (격리 신규 기능) */}
                <Route
                  path="/workmate/exam-supervision"
                  element={
                    <RequireAuth>
                      <ExamSupervisionList />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/exam-supervision/:projectId"
                  element={
                    <RequireAuth>
                      <ExamSupervisionDetail />
                    </RequireAuth>
                  }
                />
                {/* Office Builder (workmate 소속, 인증 필요) */}
                <Route
                  path="/workmate/office-builder"
                  element={
                    <RequireAuth>
                      <OfficeBuilderListPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workmate/office-builder/:pageId"
                  element={
                    <RequireAuth>
                      <OfficeBuilderEditorPage />
                    </RequireAuth>
                  }
                />

                {/* Informate */}
                <Route path="/informate/schools" element={<InformateSchoolPreview />} />
                <Route path="/informate/schools/ask" element={<RequireAuth><AskSchoolQuestion /></RequireAuth>} />
                <Route path="/informate/schools/questions" element={<RequireAuth><SchoolQuestionInbox /></RequireAuth>} />
                <Route path="/informate/schools/questions/:questionId" element={<RequireAuth><SchoolQuestionDetail /></RequireAuth>} />
                <Route path="/informate/books" element={<InformateBooks />} />
                <Route
                  path="/informate/books/submit"
                  element={
                    <RequireAuth>
                      <InformateBookSubmit />
                    </RequireAuth>
                  }
                />
                <Route path="/informate/books/:bookId" element={<InformateBookDetail />} />

                <Route path="/informate/youtubers" element={<InformateYoutubers />} />
                <Route
                  path="/informate/youtubers/submit"
                  element={
                    <RequireAuth>
                      <InformateYoutuberSubmit />
                    </RequireAuth>
                  }
                />
                <Route path="/informate/youtubers/:youtuberId" element={<InformateYoutuberDetail />} />

                <Route path="/informate/blogers" element={<InformateBlogers />} />
                <Route
                  path="/informate/blogers/submit"
                  element={
                    <RequireAuth>
                      <InformateBlogerSubmit />
                    </RequireAuth>
                  }
                />

                <Route path="/informate/mbti" element={<InformateMbti />} />
                <Route path="/informate/openchats" element={<InformateOpenChats />} />
                <Route path="/informate/vibe-sites" element={<InformateVibeSites />} />
                <Route path="/informate/vibe-sites/new" element={<InformateVibeSiteForm />} />
                <Route path="/informate/vibe-sites/:siteId/edit" element={<InformateVibeSiteForm />} />
                <Route path="/informate/vibe-sites/:siteId" element={<InformateVibeSiteBrowse />} />
                <Route
                  path="/classmate/classrooms"
                  element={
                    <RequireAuth>
                      <ClassmateClassrooms />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/classrooms/:id"
                  element={
                    <RequireAuth>
                      <ClassmateClassroomDetail />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/arrangements"
                  element={
                    <RequireAuth>
                      <ClassmateArrangements />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/arrangements/:id"
                  element={
                    <RequireAuth>
                      <ClassmateArrangementDetail />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/activities"
                  element={
                    <RequireAuth>
                      <ClassmateActivities />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/random-pick"
                  element={
                    <RequireAuth>
                      <ClassmateRandomPick />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/random-pick/library"
                  element={
                    <RequireAuth>
                      <ClassmateRandomPickLibrary />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/random-pick/sets/new"
                  element={
                    <RequireAuth>
                      <ClassmateRandomPickSetEditor />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/random-pick/sets/:setId/edit"
                  element={
                    <RequireAuth>
                      <ClassmateRandomPickSetEditor />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/progress"
                  element={
                    <RequireAuth>
                      <ClassmateProgress />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/progress/:progressId"
                  element={
                    <RequireAuth>
                      <ClassmateProgressDetail />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/attendance/:entryId"
                  element={
                    <RequireAuth>
                      <ClassmateAttendance />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/attendance-management"
                  element={
                    <RequireAuth>
                      <ClassmateAttendanceManagement />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/attendance-management/register/:registerId"
                  element={
                    <RequireAuth>
                      <ClassmateAttendanceRegister />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/attendance-management/:classroomId"
                  element={
                    <RequireAuth>
                      <ClassmateAttendanceDetail />
                    </RequireAuth>
                  }
                />
                <Route path="/classmate/votes" element={<ClassmateVotes />} />
                <Route
                  path="/classmate/votes/:voteId"
                  element={
                    <RequireAuth>
                      <ClassmateVoteScreen />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/votes/:voteId/result"
                  element={
                    <RequireAuth>
                      <ClassmateVoteResultScreen />
                    </RequireAuth>
                  }
                />
                <Route path="/classmate/games" element={<ClassmateGames />} />
                <Route
                  path="/classmate/games/create"
                  element={
                    <RequireAuth>
                      <ClassmateGameCreate />
                    </RequireAuth>
                  }
                />
                <Route path="/classmate/games/:gameId" element={<ClassmateGameDetail />} />
                <Route path="/classmate/games/:gameId/edit" element={<ClassmateGameEdit />} />
                <Route path="/classmate/games/:gameId/play" element={<ClassmateGamePlay />} />
                <Route path="/classmate/quiz-battle" element={<QuizBattleGames />} />
                <Route path="/classmate/quiz-battle/:setId/together" element={<RequireAuth><QuizStudioSetup /></RequireAuth>} />
                <Route path="/classmate/quiz-battle/create" element={<QuizBattleCreate />} />
                <Route path="/classmate/quiz-battle/:setId/edit" element={<QuizBattleEdit />} />
                <Route path="/classmate/quiz-battle/:setId" element={<QuizBattleDetail />} />
                <Route
                  path="/classmate/quiz-battle/live/:setId"
                  element={
                    <RequireAuth>
                      <QuizBattleLiveDashboard />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/quiz-battle/live/:setId/session/:sessionId"
                  element={
                    <RequireAuth>
                      <QuizBattleLiveSessionResult />
                    </RequireAuth>
                  }
                />
                <Route path="/classmate/qr" element={<ClassmateQr />} />
                <Route path="/classmate/timer" element={<ClassmateTimer />} />
                <Route
                  path="/classmate/board"
                  element={
                    <RequireAuth>
                      <ClassmateBoard />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/sociometry"
                  element={<SociometryRoute />}
                />
                <Route
                  path="/classmate/sociometry/:surveyId"
                  element={
                    <RequireAuth>
                      <ClassmateSociometryDashboard />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/albums"
                  element={<RequireAuth><ClassmateAlbumList /></RequireAuth>}
                />
                <Route
                  path="/classmate/walls"
                  element={<RequireAuth><ClassmateWallList /></RequireAuth>}
                />
                <Route
                  path="/classmate/wordcloud"
                  element={
                    <RequireAuth>
                      <ClassmateWordCloudList />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/wordcloud/:id"
                  element={
                    <RequireAuth>
                      <ClassmateWordCloudDashboard />
                    </RequireAuth>
                  }
                />
                {/* Reading Assessment (스피치 발음 평가 — 격리된 신규 기능) */}
                <Route
                  path="/classmate/reading-assessment"
                  element={
                    <RequireAuth>
                      <ClassmateReadingAssessmentList />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/reading-assessment/:taskId"
                  element={
                    <RequireAuth>
                      <ClassmateReadingAssessmentDashboard />
                    </RequireAuth>
                  }
                />
                {/* Essay Grading (논술형 평가 — 격리된 신규 기능) */}
                <Route
                  path="/classmate/essay-grading"
                  element={
                    <RequireAuth>
                      <ClassmateEssayGradingList />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/essay-grading/:assessmentId"
                  element={
                    <RequireAuth>
                      <ClassmateEssayGradingDetail />
                    </RequireAuth>
                  }
                />
                {/* 발표 버저 */}
                <Route
                  path="/classmate/buzzer"
                  element={<ClassmateBuzzerList />}
                />
                <Route path="/classmate/buzzer/:id" element={<ClassmateBuzzerHost />} />
                <Route
                  path="/classmate/onepick"
                  element={<ClassmateOnepickList />}
                />
                <Route
                  path="/classmate/onepick/:id"
                  element={<ClassmateOnepickDashboard />}
                />
                {/* Group Match (모둠 매칭) */}
                <Route
                  path="/classmate/group-match"
                  element={
                    <RequireAuth>
                      <ClassmateGroupMatchList />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/group-match/:formId"
                  element={
                    <RequireAuth>
                      <ClassmateGroupMatchDetail />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/wrong-answer-note"
                  element={
                    <RequireAuth>
                      <ClassmateWrongAnswerNote />
                    </RequireAuth>
                  }
                />
                <Route path="/classmate/secret16" element={<ClassmateSecret16Programs />} />
                <Route
                  path="/classmate/peer-quiz"
                  element={
                    <RequireAuth>
                      <PeerQuizRoomList />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/peer-quiz/host/:roomId"
                  element={
                    <RequireAuth>
                      <PeerQuizHostDashboard />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/peer-quiz/host/:roomId/round/:sessionId"
                  element={
                    <RequireAuth>
                      <PeerQuizRoundDetail />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/secret16/create"
                  element={
                    <RequireAuth>
                      <ClassmateSecret16ProgramEditor />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/secret16/:programId/edit"
                  element={
                    <RequireAuth>
                      <ClassmateSecret16ProgramEditor />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/classmate/secret16/:programId"
                  element={<ClassmateSecret16ProgramHub />}
                />
                <Route
                  path="/classmate/secret16/:programId/host/:roomId"
                  element={
                    <RequireAuth>
                      <ClassmateSecret16HostDashboard />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <RequireAuth>
                      <Notifications />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/messages"
                  element={
                    <RequireAuth>
                      <Messages />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/messages/:messageId"
                  element={
                    <RequireAuth>
                      <MessageDetail />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/messages/compose"
                  element={
                    <RequireAuth>
                      <ComposeMessage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/account/profile"
                  element={
                    <RequireAuth>
                      <ProfileSettings />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/follows"
                  element={
                    <RequireAuth>
                      <Follows />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/points"
                  element={
                    <RequireAuth>
                      <Points />
                    </RequireAuth>
                  }
                />
                <Route path="/home" element={<Navigate to="/" replace />} />
                <Route path="*" element={<StaleRouteRecovery />} />
              </Routes>
              {background && (
                <Routes>
                  <Route
                    path="/informate/schools/questions/:questionId"
                    element={
                      <RequireAuth>
                        <SchoolQuestionDetail />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/workmate/school-conversation/questions/:questionId"
                    element={
                      <RequireAuth>
                        <SchoolQuestionDetail />
                      </RequireAuth>
                    }
                  />
                </Routes>
              )}
            </AppShell>
          } />
        </Routes>
      </Suspense>
      </PageErrorBoundary>
    </>
  );
}
